/**
 * IndexedDB Error Handler Utility
 * 
 * Handles IndexedDB connection errors, provides recovery mechanisms,
 * and implements fallback strategies for when IndexedDB is unavailable.
 */

interface IndexedDBErrorInfo {
  isIndexedDBError: boolean;
  errorType: 'connection_lost' | 'quota_exceeded' | 'blocked' | 'unknown';
  userMessage: string;
  canRetry: boolean;
  shouldRefresh: boolean;
}

/**
 * Detects if an error is related to IndexedDB issues
 */
export const isIndexedDBError = (error: any): boolean => {
  if (!error) return false;
  
  const errorMessage = typeof error === 'string' ? error : error.message || '';
  const errorName = error.name || '';
  
  // Common IndexedDB error patterns
  const indexedDBPatterns = [
    'Connection to Indexed Database server lost',
    'IndexedDB',
    'IDBDatabase',
    'IDBTransaction',
    'IDBRequest',
    'QuotaExceededError',
    'InvalidStateError',
    'UnknownError', // Safari specific
    'The operation failed for reasons unrelated to the database itself',
    'The database connection is closing',
    'The transaction was aborted'
  ];
  
  return indexedDBPatterns.some(pattern => 
    errorMessage.includes(pattern) || errorName.includes(pattern)
  );
};

/**
 * Analyzes IndexedDB errors and provides detailed information
 */
export const analyzeIndexedDBError = (error: any): IndexedDBErrorInfo => {
  const errorMessage = typeof error === 'string' ? error : error.message || '';
  const errorName = error.name || '';
  
  if (!isIndexedDBError(error)) {
    return {
      isIndexedDBError: false,
      errorType: 'unknown',
      userMessage: 'An unexpected error occurred.',
      canRetry: true,
      shouldRefresh: false
    };
  }
  
  // Connection lost errors
  if (errorMessage.includes('Connection to Indexed Database server lost') ||
      errorMessage.includes('server lost')) {
    return {
      isIndexedDBError: true,
      errorType: 'connection_lost',
      userMessage: 'Database connection was lost. Please refresh the page to reconnect.',
      canRetry: false,
      shouldRefresh: true
    };
  }
  
  // Quota exceeded errors
  if (errorName === 'QuotaExceededError' || errorMessage.includes('quota')) {
    return {
      isIndexedDBError: true,
      errorType: 'quota_exceeded',
      userMessage: 'Browser storage is full. Please clear your browser data and refresh.',
      canRetry: false,
      shouldRefresh: true
    };
  }
  
  // Blocked database errors
  if (errorName === 'BlockedError' || errorMessage.includes('blocked')) {
    return {
      isIndexedDBError: true,
      errorType: 'blocked',
      userMessage: 'Database is blocked. Please close other tabs and refresh.',
      canRetry: false,
      shouldRefresh: true
    };
  }
  
  // Generic IndexedDB errors
  return {
    isIndexedDBError: true,
    errorType: 'unknown',
    userMessage: 'Database error occurred. Please refresh the page to continue.',
    canRetry: false,
    shouldRefresh: true
  };
};

/**
 * Attempts to recover from IndexedDB errors
 */
export const attemptIndexedDBRecovery = async (): Promise<boolean> => {
  try {
    // Clear any corrupted IndexedDB data
    if ('indexedDB' in window) {
      // Try to open a test database to check if IndexedDB is working
      const testDB = await new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open('socialness-test-db', 1);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
        request.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;
          if (!db.objectStoreNames.contains('test')) {
            db.createObjectStore('test');
          }
        };
      });
      
      // Close and delete the test database
      testDB.close();
      await new Promise<void>((resolve, reject) => {
        const deleteRequest = indexedDB.deleteDatabase('socialness-test-db');
        deleteRequest.onerror = () => reject(deleteRequest.error);
        deleteRequest.onsuccess = () => resolve();
      });
      
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('IndexedDB recovery failed:', error);
    return false;
  }
};

/**
 * Shows a user-friendly error modal for IndexedDB issues
 */
export const showIndexedDBErrorModal = (errorInfo: IndexedDBErrorInfo): void => {
  // Create a custom modal for IndexedDB errors
  const modal = document.createElement('div');
  modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
  modal.innerHTML = `
    <div class="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
      <div class="flex items-center mb-4">
        <svg class="w-8 h-8 text-red-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 15.5c-.77.833.192 2.5 1.732 2.5z"></path>
        </svg>
        <h2 class="text-lg font-semibold text-gray-900">Database Connection Issue</h2>
      </div>
      
      <p class="text-gray-600 mb-4">${errorInfo.userMessage}</p>
      
      <div class="flex justify-end space-x-3">
        ${errorInfo.canRetry ? `
          <button id="retry-btn" class="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50">
            Try Again
          </button>
        ` : ''}
        <button id="refresh-btn" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          ${errorInfo.shouldRefresh ? 'Refresh Page' : 'OK'}
        </button>
      </div>
    </div>
  `;
  
  document.body.appendChild(modal);
  
  // Add event listeners
  const refreshBtn = modal.querySelector('#refresh-btn');
  const retryBtn = modal.querySelector('#retry-btn');
  
  refreshBtn?.addEventListener('click', () => {
    if (errorInfo.shouldRefresh) {
      window.location.reload();
    } else {
      document.body.removeChild(modal);
    }
  });
  
  retryBtn?.addEventListener('click', async () => {
    const recovered = await attemptIndexedDBRecovery();
    if (recovered) {
      document.body.removeChild(modal);
      window.location.reload();
    } else {
      // Update modal to show refresh option
      const retryBtnElement = modal.querySelector('#retry-btn') as HTMLButtonElement;
      if (retryBtnElement) {
        retryBtnElement.textContent = 'Recovery Failed';
        retryBtnElement.disabled = true;
      }
    }
  });
};

/**
 * Global error handler for IndexedDB errors
 */
export const handleIndexedDBError = (error: any, context?: string): void => {
  const errorInfo = analyzeIndexedDBError(error);
  
  if (errorInfo.isIndexedDBError) {
    console.error(`IndexedDB Error in ${context || 'unknown context'}:`, error);
    
    // Send to Sentry with specific tags
    if (window.Sentry) {
      try {
        window.Sentry.captureException(error, {
          tags: {
            errorType: 'indexeddb',
            errorSubtype: errorInfo.errorType,
            context: context || 'unknown',
            userAgent: navigator.userAgent.substring(0, 100), // Truncate to avoid too long tags
            url: window.location.pathname
          }
        });
      } catch (sentryError) {
        // Fallback if Sentry fails
        console.error('Failed to send IndexedDB error to Sentry:', sentryError);
      }
    }
    
    // Show user-friendly error modal
    showIndexedDBErrorModal(errorInfo);
  }
};

/**
 * Wraps async functions to catch and handle IndexedDB errors
 */
export const withIndexedDBErrorHandling = <T extends (...args: any[]) => Promise<any>>(
  fn: T,
  context?: string
): T => {
  return (async (...args: Parameters<T>) => {
    try {
      return await fn(...args);
    } catch (error) {
      if (isIndexedDBError(error)) {
        handleIndexedDBError(error, context);
        throw error; // Re-throw so React Query can handle it
      }
      throw error;
    }
  }) as T;
}; 